/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TimestampProps {
  id: string;
  time: Date;
  userId: string | null;
  note: string | null;
  typeId: string;
  cardId: string;
  requestId: string | null;
  operationId: string | null;
  personsInvolved: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TimestampRecord extends Record<TimestampProps>({
  id: null as any,
  time: null as any,
  userId: null as any,
  note: null as any,
  typeId: null as any,
  cardId: null as any,
  requestId: null as any,
  operationId: null as any,
  personsInvolved: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    time: string | number | Date;
    userId: string | null;
    note: string | null;
    typeId: string;
    cardId: string;
    requestId: string | null;
    operationId: string | null;
    personsInvolved: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TimestampRecord {
    if (json == null) {
      throw new Error('TimestampRecord.fromJS: parameter cannot be null');
    }
    return new TimestampRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TimestampJson.id',
      ),
      time: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'time', '0001-01-01T00:00:00.00Z'),
          'TimestampJson.time',
        ),
      ),
      userId: json.userId,
      note: json.note,
      typeId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'typeId', '00000000-0000-0000-0000-000000000000'),
        'TimestampJson.typeId',
      ),
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'TimestampJson.cardId',
      ),
      requestId: json.requestId,
      operationId: json.operationId,
      personsInvolved: json.personsInvolved,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TimestampJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    time: string | number | Date;
    userId: string | null;
    note: string | null;
    typeId: string;
    cardId: string;
    requestId: string | null;
    operationId: string | null;
    personsInvolved: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TimestampRecord, Error[]] {
    if (json == null) {
      throw new Error('TimestampRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TimestampRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TimestampJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      time: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'time', '0001-01-01T00:00:00.00Z'),
              'TimestampJson.time',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      userId: tryOrDefault(
        () => json.userId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      note: tryOrDefault(
        () => json.note,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      typeId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'typeId', '00000000-0000-0000-0000-000000000000'),
            'TimestampJson.typeId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'TimestampJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () => json.requestId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationId: tryOrDefault(
        () => json.operationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personsInvolved: tryOrDefault(
        () => json.personsInvolved,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TimestampJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TimestampProps) {
    super(props);
    if (props == null) {
      throw new Error('TimestampRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TimestampViewModelBase implements TimestampProps {
  protected readonly _props: TimestampProps;
  private _json?: TimestampProps;

  get id(): string {
    return this._props.id;
  }
  get time(): Date {
    return this._props.time;
  }
  get userId(): string | null {
    return this._props.userId;
  }
  get note(): string | null {
    return this._props.note;
  }
  get typeId(): string {
    return this._props.typeId;
  }
  get cardId(): string {
    return this._props.cardId;
  }
  get requestId(): string | null {
    return this._props.requestId;
  }
  get operationId(): string | null {
    return this._props.operationId;
  }
  get personsInvolved(): string | null {
    return this._props.personsInvolved;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TimestampProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        time: this._props.time,
        userId: this._props.userId,
        note: this._props.note,
        typeId: this._props.typeId,
        cardId: this._props.cardId,
        requestId: this._props.requestId,
        operationId: this._props.operationId,
        personsInvolved: this._props.personsInvolved,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTimestampEntities = (state: AppState) =>
  state.entityStores.timestamp.entities;

export const selectTimestampById = (id: string) => (state: AppState) =>
  state.entityStores.timestamp.entities.get(id);

export const selectTimestampMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.timestamp.meta.get(id);

export const selectTimestampAndMetadataById = (id: string) =>
  createSelector(selectTimestampById(id), selectTimestampMetadataById(id), (timestamp, meta) => ({
    timestamp,
    meta,
  }));

export function selectTimestampEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTimestampEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TimestampRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTimestampEntitiesByTime(time: Date) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.time === time).valueSeq(),
  );
}

export function selectTimestampEntitiesByUserId(userId: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.userId === userId).valueSeq(),
  );
}

export function selectTimestampEntitiesByNote(note: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.note === note).valueSeq(),
  );
}

export function selectTimestampEntitiesByTypeId(typeId: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.typeId === typeId).valueSeq(),
  );
}

export function selectTimestampEntitiesByCardId(cardId: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectTimestampEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectTimestampEntitiesByOperationId(operationId: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.operationId === operationId).valueSeq(),
  );
}

export function selectTimestampEntitiesByPersonsInvolved(personsInvolved: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.personsInvolved === personsInvolved).valueSeq(),
  );
}

export function selectTimestampEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTimestampEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
